/**
 * Demo dev `?interact=1` — pickup / drag / give / consume na placeholderach.
 * 30 obiektów, cel 60 fps. Mock psa (InteractTarget) bez importu avatar/.
 */

import Phaser from 'phaser';
import { GAME_HEIGHT, GAME_WIDTH } from '@pieskowo/shared';
import { ObjectLayer } from './ObjectLayer.js';
import type { InteractTarget, ObjectDef, SnapZone, SpawnPlacement } from './types.js';

export class InteractDemoScene extends Phaser.Scene {
  static readonly KEY = 'InteractDemo';

  private layer: ObjectLayer | null = null;
  private mockDog: MockDog | null = null;
  private fpsText: Phaser.GameObjects.Text | null = null;
  private logText: Phaser.GameObjects.Text | null = null;
  private lastLog = '';

  constructor() {
    super(InteractDemoScene.KEY);
  }

  create(): void {
    this.cameras.main.setBackgroundColor(0xfff3e9);
    this.add
      .rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0xe8f5e9)
      .setDepth(-100);

    // podłoga
    this.add
      .rectangle(GAME_WIDTH / 2, GAME_HEIGHT - 40, GAME_WIDTH, 80, 0xc8e6c9)
      .setDepth(-50);

    this.add
      .text(24, 16, 'Demo interakcji (?interact=1) — przeciągaj, podnoś, dawaj, jedz', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '22px',
        color: '#4a3f55',
        fontStyle: 'bold',
      })
      .setDepth(20_000);

    this.fpsText = this.add
      .text(GAME_WIDTH - 16, 16, '', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '16px',
        color: '#6b5f7a',
      })
      .setOrigin(1, 0)
      .setDepth(20_000);

    this.logText = this.add
      .text(24, GAME_HEIGHT - 28, '', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '16px',
        color: '#5d4e6d',
      })
      .setDepth(20_000);

    this.mockDog = new MockDog(this, 200, 480);
    const npc = new MockDog(this, 900, 420, 0x90caf9, 'NPC');

    const defs = buildDemoDefs();
    const placements = buildDemoPlacements(defs);

    this.layer = new ObjectLayer({
      scene: this,
      defs,
      target: this.mockDog,
      giveTargets: [npc],
      interactRange: 100,
    });

    const zones: SnapZone[] = [
      { id: 'miska', x: 320, y: 520, radius: 56 },
      { id: 'stolik', x: 640, y: 360, radius: 64 },
      { id: 'talerz', x: 980, y: 500, radius: 52 },
    ];
    this.layer.setSnapZones(zones);
    for (const z of zones) {
      const c = this.add.circle(z.x, z.y, z.radius, 0xffffff, 0.25);
      c.setStrokeStyle(2, 0x81c784, 0.8);
      c.setDepth(0);
      this.add
        .text(z.x, z.y + z.radius + 8, z.id, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '14px',
          color: '#558b2f',
        })
        .setOrigin(0.5, 0);
    }

    this.layer.spawn(placements);

    this.layer.on((evt) => {
      this.lastLog = `${evt.type} · ${evt.defId} (${Math.round(evt.x)},${Math.round(evt.y)})`;
      if (this.logText) this.logText.setText(this.lastLog);
    });

    // tap na ziemię = pies idzie
    this.input.on('pointerdown', (p: Phaser.Input.Pointer) => {
      if (p.event && (p.event as Event).target !== this.game.canvas) return;
      // jeśli trafiono obiekt — ObjectLayer/InteractiveObject już obsłuży
      const hit = this.layer
        ?.getObjects()
        .some((o) => {
          const s = o.container;
          const b = s.getBounds();
          return b.contains(p.worldX, p.worldY);
        });
      if (!hit && this.mockDog) {
        this.mockDog.moveTo(p.worldX, p.worldY);
      }
    });
  }

  update(_t: number, dtMs: number): void {
    const dt = Math.min(0.05, dtMs / 1000);
    this.mockDog?.update(dt);
    this.layer?.update(dt);
    if (this.fpsText) {
      const fps = Math.round(this.game.loop.actualFps);
      const n = this.layer?.getObjects().length ?? 0;
      this.fpsText.setText(`${fps} fps · ${n} obiektów`);
    }
  }

  shutdown(): void {
    this.layer?.destroy();
    this.layer = null;
    this.mockDog?.destroy();
    this.mockDog = null;
  }
}

/** Prosty mock psa — InteractTarget. */
class MockDog implements InteractTarget {
  x: number;
  y: number;
  isMoving = false;
  heldItem: string | null = null;
  private readonly body: Phaser.GameObjects.Container;
  private readonly scene: Phaser.Scene;
  private goalX: number;
  private goalY: number;
  private readonly speed = 220;
  private mouth: Phaser.GameObjects.Text;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    color = 0xffcc80,
    label = 'Pies',
  ) {
    this.scene = scene;
    this.x = x;
    this.y = y;
    this.goalX = x;
    this.goalY = y;
    const circle = scene.add.circle(0, 0, 36, color).setStrokeStyle(3, 0xffffff);
    const face = scene.add
      .text(0, -4, '🐶', { fontSize: '28px' })
      .setOrigin(0.5);
    const tag = scene.add
      .text(0, 40, label, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '14px',
        color: '#4a3f55',
      })
      .setOrigin(0.5, 0);
    this.mouth = scene.add.text(22, -8, '', { fontSize: '18px' }).setOrigin(0.5);
    this.body = scene.add.container(x, y, [circle, face, tag, this.mouth]);
    this.body.setDepth(y + 1);
  }

  getPosition(): { x: number; y: number } {
    return { x: this.x, y: this.y };
  }

  moveTo(x: number, y: number): void {
    this.goalX = x;
    this.goalY = y;
    this.isMoving = true;
  }

  holdItem(textureKey: string | null): void {
    this.heldItem = textureKey;
    this.mouth.setText(textureKey ? '🍪' : '');
  }

  async playAction(
    a: string,
  ): Promise<void> {
    const scale = a === 'jump' ? 1.25 : a === 'eat' ? 0.9 : 1.12;
    return new Promise((resolve) => {
      this.scene.tweens.add({
        targets: this.body,
        scaleX: scale,
        scaleY: scale,
        duration: 160,
        yoyo: true,
        onComplete: () => resolve(),
      });
    });
  }

  update(dt: number): void {
    if (!this.isMoving) return;
    const dx = this.goalX - this.x;
    const dy = this.goalY - this.y;
    const d = Math.hypot(dx, dy);
    if (d < 6) {
      this.x = this.goalX;
      this.y = this.goalY;
      this.isMoving = false;
    } else {
      const step = this.speed * dt;
      this.x += (dx / d) * Math.min(step, d);
      this.y += (dy / d) * Math.min(step, d);
    }
    this.body.setPosition(this.x, this.y);
    this.body.setDepth(this.y + 1);
  }

  destroy(): void {
    this.body.destroy(true);
  }
}

function buildDemoDefs(): ObjectDef[] {
  return [
    {
      id: 'ciastko',
      texture: 'obj_ciastko',
      size: 72,
      interactions: ['pickup', 'give', 'consume', 'drag'],
      states: ['full', 'empty'],
      fx: 'crumbs',
      dogReaction: 'eat',
      snapTargets: ['miska', 'talerz', 'stolik'],
    },
    {
      id: 'pilka',
      texture: 'obj_pilka',
      size: 68,
      interactions: ['bounce', 'drag'],
      fx: 'stars',
      dogReaction: 'jump',
    },
    {
      id: 'dzwonek',
      texture: 'obj_dzwonek',
      size: 64,
      interactions: ['tap', 'toggle'],
      states: ['on', 'off'],
      fx: 'notes',
      dogReaction: 'happy',
    },
    {
      id: 'kwiatek',
      texture: 'obj_kwiatek',
      size: 64,
      interactions: ['tap'],
      fx: 'sparkle',
      dogReaction: 'sniff',
    },
    {
      id: 'miska_karmy',
      texture: 'obj_miska',
      size: 80,
      interactions: ['tap', 'consume'],
      states: ['full', 'empty'],
      fx: 'hearts',
      dogReaction: 'eat',
      snapTargets: ['miska'],
    },
    {
      id: 'kubek',
      texture: 'obj_kubek',
      size: 64,
      interactions: ['drag', 'pickup', 'give'],
      fx: 'splash',
      dogReaction: 'happy',
      snapTargets: ['stolik', 'talerz'],
    },
  ];
}

/** 30 obiektów rozłożonych na planszy. */
function buildDemoPlacements(defs: ObjectDef[]): SpawnPlacement[] {
  const out: SpawnPlacement[] = [];
  const ids = defs.map((d) => d.id);
  for (let i = 0; i < 30; i++) {
    const defId = ids[i % ids.length]!;
    const col = i % 10;
    const row = Math.floor(i / 10);
    out.push({
      defId,
      x: 120 + col * 110 + (row % 2) * 20,
      y: 160 + row * 100,
    });
  }
  return out;
}
